import React, { useEffect } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import Page from 'flow/components/Setting/Page';
import { LocationType } from 'flow/types/Location';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { navigateBack } from 'flow/actions/navigate-actions';
import DashboardImage from 'images/ai-studio/ai_receptionist_dashboard.png';
import useAiStudioSettings from './hooks/useAiStudioSettings';
import LoadingOverlay from 'flow/components/ui-components/LoadingOverlay';
import useHasAiStudioAccess from './hooks/useHasAiStudioAccess';
import InformationBottomSection from './components/InformationBottomSection';
import ActionSection from './components/ActionSection';

const AiStudioPage = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const handleBack = () => dispatch(navigateBack(history.location));

    const {
        getAuthCode,
        isAuthcodeLoading,
        authCodeError,
        redirections,
        authCodeData,
        clearAuthCode,
    } = useAiStudioSettings();

    const { isUserAccessLoading, hasUserAccessAiStudio } =
        useHasAiStudioAccess();

    const openNewWindow = (url: string) =>
        window.open(url, '_blank', 'noopener,noreferrer');

    useEffect(() => {
        const authCode = authCodeData?.authorizationCode;
        const dashboardUrl = redirections?.dashboard;
        if (authCode && dashboardUrl) {
            openNewWindow(`${dashboardUrl}?code=${authCode}`);
            clearAuthCode();
        }
    }, [
        authCodeData?.authorizationCode,
        redirections?.dashboard,
        clearAuthCode,
    ]);

    useEffect(() => {
        if (!hasUserAccessAiStudio && !isUserAccessLoading) {
            history.replace('/');
        }
    }, [hasUserAccessAiStudio, isUserAccessLoading, history]);

    if (!hasUserAccessAiStudio && !isUserAccessLoading) {
        return null;
    }

    return (
        <Page
            header={i18next.t('ai-studio.main_title_top')}
            onClickBack={handleBack}
            childrenClassName="w-full"
            mainClassName="p-0 bg-gray-200 flex"
            className="no-main-margin h-full"
        >
            <div className="flex w-full justify-center py-4 leading-relaxed text-gray-900 md:py-10">
                <div className="max-w-[1200px] rounded-none bg-white px-4 py-4 md:rounded-2xl md:px-15 md:py-10">
                    <div className="mx-auto grid items-center gap-12 max-md:grid-cols-1 max-md:text-center lg:grid-cols-2 lg:text-left">
                        <ActionSection
                            redirections={redirections}
                            openNewWindow={openNewWindow}
                            getAuthCode={getAuthCode}
                        />
                        <div className="flex justify-center max-md:mt-4">
                            <img
                                src={DashboardImage}
                                alt={i18next.t('ai-studio.hero.dashboard_alt')}
                                className="w-full max-w-[540px] border border-gray-100 shadow-[0_16px_48px_rgba(0,0,0,0.06)]"
                            />
                        </div>
                    </div>
                    <InformationBottomSection />
                </div>
            </div>
            {isAuthcodeLoading && !authCodeError ? <LoadingOverlay /> : null}
        </Page>
    );
};

export default AiStudioPage;
